import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

import { assistantApi, QuotaExceededError } from "./api";
import { ASSISTANT_QUERY_KEY } from "./constants";
import type { BubbleMessage, QuotaError } from "./types";

const EMPTY: BubbleMessage = {
  role: "assistant",
  content: "",
  blocks: [],
  toolLabel: null,
  error: null,
};

const INTERRUPTED = "A resposta foi interrompida.";

export function useAssistantStream(conversationId: number | null) {
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<BubbleMessage | null>(null);
  const [pendingUser, setPendingUser] = useState<string | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [quota, setQuota] = useState<QuotaError | null>(null);
  const lastSent = useRef<string | null>(null);
  const controller = useRef<AbortController | null>(null);
  // Conversa do stream em andamento: a troca para OUTRA conversa descarta o estado;
  // a troca para esta (primeira mensagem, id recém-criado) mantém.
  const activeId = useRef<number | null>(null);

  useEffect(() => {
    if (conversationId === activeId.current) return;
    controller.current?.abort();
    activeId.current = null;
    setPending(null);
    setPendingUser(null);
    setQuota(null);
    setIsStreaming(false);
    lastSent.current = null;
  }, [conversationId]);

  useEffect(() => () => controller.current?.abort(), []);

  const send = useCallback(
    async (content: string, idOverride?: number) => {
      const id = idOverride ?? conversationId;
      if (id === null || isStreaming) return;
      activeId.current = id;
      lastSent.current = content;
      setPendingUser(content);
      setPending({ ...EMPTY });
      setIsStreaming(true);
      setQuota(null);
      controller.current = new AbortController();
      let finished = false;

      try {
        await assistantApi.streamMessage(
          id,
          content,
          (event) => {
            // Fora do updater: o React pode rodar o updater só no próximo render.
            if (event.event === "done") finished = true;
            setPending((prev) => {
              const current = prev ?? { ...EMPTY };
              switch (event.event) {
                case "delta":
                  return {
                    ...current,
                    content: current.content + event.data.text,
                    toolLabel: null,
                  };
                case "tool_start":
                  return { ...current, toolLabel: event.data.label };
                case "tool_end":
                  return { ...current, toolLabel: null };
                case "blocks":
                  return { ...current, blocks: event.data };
                case "done":
                  return {
                    ...current,
                    id: event.data.message_id,
                    toolLabel: null,
                  };
                case "error":
                  return {
                    ...current,
                    toolLabel: null,
                    error: event.data.message,
                  };
                default:
                  return current;
              }
            });
          },
          controller.current.signal,
        );
        if (!finished) {
          setPending((prev) =>
            prev && !prev.error ? { ...prev, error: INTERRUPTED } : prev,
          );
        }
      } catch (error) {
        if (error instanceof QuotaExceededError) {
          setQuota(error.quota);
          setPending(null);
          setPendingUser(null);
        } else if ((error as Error).name !== "AbortError") {
          // Erro de rede/parse vira mensagem amigável; o original vai para o console (e Sentry).
          console.error("[assistant] stream failed", error);
          const message = /Aguarde um minuto/.test(
            (error as Error).message ?? "",
          )
            ? (error as Error).message
            : INTERRUPTED;
          setPending((prev) => ({ ...(prev ?? EMPTY), error: message }));
        }
      } finally {
        setIsStreaming(false);
        // Sempre reconsultar: mesmo numa queda, a API pode ter persistido o turno.
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: ["assistant", "conversation", id],
          }),
          queryClient.invalidateQueries({ queryKey: ASSISTANT_QUERY_KEY }),
        ]);
        if (finished) {
          setPending(null);
          setPendingUser(null);
        }
      }
    },
    [conversationId, isStreaming, queryClient],
  );

  const retry = useCallback(async () => {
    if (lastSent.current) await send(lastSent.current);
  }, [send]);

  const abort = useCallback(() => controller.current?.abort(), []);

  return { pending, pendingUser, isStreaming, quota, send, retry, abort };
}
